import React, { useEffect, useState } from 'react';
import {
    Container,
    SpaceBetween,
    Header,
    Button,
    Table,
    Toggle,
    Box,
    ExpandableSection,
    Link,
} from '@amzn/awsui-components-react';
import "../../css/errorSummary.css";
import { getUserAlias } from "../../utils/utils";
import { modifyWorkflowStatus } from "../redux/exceptionAPIs";
import { TransactionsAdjustment } from './MOGTransactionsAdjustment';
import {get, isNull, isUndefined, set} from 'lodash';
import { ACKNOWLEDGEMENT_STRUCTURE_MAP_KEY, ASM_COMPLETE_STATUS, PHASTOS_OBLIGATION } from '../common/constants';
import { getExceptionTransactionDetails, showTransactionsHandler } from '../common/adjustment/constants/errorSummaryUtils';
import { getColumnDefinitions } from './constants/errorSummaryConstants';
import {TRANSACTION_CONTAINER_DOM_ID} from "./constants/boxLevelConstants";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {getExceptionSeverityMapping, getExceptionStatusMapping} from '../../utils/utils';
import AppConfig from "../../../../AppConfig";
import QuickSightFrame from "../common/QuickSightFrame";

export default function MOGErrorSummary(props) {
    const { 
        exceptionData,
        isUnderReview,
        obligationStatus,
        stepsState,
        setStepsStateHandler,
        updateWorkflowStatusDispatcher,
        parentWorkflowId,
        transactionReviewEditMode,
        setTransactionReviewEditMode,
        transactionSummaryData,
        isTransactionReviewLoading,
        setIsTransactionReviewLoading,
        useCase,
        reportingPeriod,
        useCaseType,
        phastosUseCaseName,
        domain,
        phastosObligation
    } = props;

    const isCompleteButtonDisabled = () => {
        return !(isUnderReview && !stepsState.transactionReviewStep.completed);
    };

    const [completeButtonState, setCompleteButtonState] = useState(false);

    const completeButtonClickHandler = async () => {
        setCompleteButtonState(true);
        const userAlias = await getUserAlias();
        const request = {
            parentWorkflowId: parentWorkflowId,
            acknowledgementStructureMap: {
                [ACKNOWLEDGEMENT_STRUCTURE_MAP_KEY.TransactionReview]: { status: ASM_COMPLETE_STATUS, user: userAlias }
            },
        };
        const onSuccess = () => {
            setStepsStateHandler("transactionReviewStep");
        };
        modifyWorkflowStatus(updateWorkflowStatusDispatcher, request, onSuccess);
        setCompleteButtonState(false);
    };

    const [items, setItems] = useState([]);

    const updateExceptionStatus = (items) => {
        const exceptionStatusMapping = getExceptionStatusMapping(PHASTOS_OBLIGATION.MOG);
        const exceptionSeverityMapping = getExceptionSeverityMapping(PHASTOS_OBLIGATION.MOG);
        const result = [];
        for (const item of items) {
            set(item, 'exceptionStatusName', exceptionStatusMapping[get(item, 'exceptionStatus', '')]);
            set(item, 'severityName', exceptionSeverityMapping[get(item, 'severity', '')]);
            result.push(item);
        }
        return result;
    };

    const refreshSummaryData = (showTransactions) => {
        setIsTransactionReviewLoading(true);
        setShowTransactions(showTransactions);

        const request = {
            parentWorkflowId: parentWorkflowId,
            setItems: setItems,
            setIsLoading: setIsTransactionReviewLoading,
            useCase: useCase,
            phastosObligation: phastosObligation
        };
        getExceptionTransactionDetails(request);
    };

    useEffect(() => {
        if (isUndefined(transactionSummaryData) || isNull(transactionSummaryData)) {
            setIsTransactionReviewLoading(true);
        } else {
            setItems(transactionSummaryData);
            setIsTransactionReviewLoading(false);
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (transactionSummaryData === undefined || transactionSummaryData.length < 1) { return }
        setItems(transactionSummaryData);
        setIsTransactionReviewLoading(false);
    }, [transactionSummaryData, setIsTransactionReviewLoading]);

    const handleShowTransactions = (params) => {
        setSummaryExpanded(false);
        showTransactionsHandler(params, setCurrentFilter, setTransactionFilterData, setShowTransactions, PHASTOS_OBLIGATION.MOG);
    };

    const columnDefinitions = getColumnDefinitions(handleShowTransactions, exceptionData?.currencySymbol);

    const [showTransactions, setShowTransactions] = useState(false);
    const [transactionFilterData, setTransactionFilterData] = useState({});

    const hideTransactionsHandler = () => {
        setShowTransactions(false);
    };

    const [currentFilter, setCurrentFilter] = useState('');
    const [isSummaryExpanded, setSummaryExpanded] = useState(true);

    useEffect(() => {
        if(isSummaryExpanded && !isTransactionReviewLoading) {
            refreshSummaryData(showTransactions);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSummaryExpanded]);

    const {
        items: dataItems,
        collectionProps
    } = useCollection(
        updateExceptionStatus(items),
        {}
    );


    return (
        <SpaceBetween size='xl'>
            <Container header={
                <Header actions={
                    <SpaceBetween size='m' direction='horizontal'>
                        <Toggle
                            onChange={({ detail }) => {
                                setTransactionReviewEditMode(detail.checked);
                            }}
                            checked={transactionReviewEditMode}>
                            Edit Mode
                        </Toggle>
                        <Button disabled={isCompleteButtonDisabled() || completeButtonState}
                                onClick={completeButtonClickHandler} variant="primary"
                        >Mark as complete</Button>
                    </SpaceBetween>
                }>Transaction review summary
                </Header>
            }>
            { transactionReviewEditMode &&
                <SpaceBetween size='xl'>
                    <ExpandableSection
                        variant="container"
                        expanded={isSummaryExpanded}
                        onChange={(e) => { setSummaryExpanded(e.detail.expanded) }}
                        header={
                            <Header actions={
                                <SpaceBetween size='s' direction='horizontal'>
                                    <div onClick={(e) => { e.stopPropagation() }}>
                                        <Link
                                            href={AppConfig[domain].MOG_SHIELD_DTREE_URL}
                                            external={true}
                                        >View Rules</Link>
                                    </div>
                                    <div onClick={(e) => { e.stopPropagation() }}>
                                        <Button variant='normal'
                                                onClick={e => {
                                                    setSummaryExpanded(true);
                                                    refreshSummaryData(false);
                                                }}
                                                disabled={isTransactionReviewLoading}
                                        >Refresh</Button>
                                    </div>
                                </SpaceBetween>
                            }>Breakdown</Header>
                        }
                    >
                        <Table
                            columnDefinitions={columnDefinitions}
                            items={dataItems}
                            loading={isTransactionReviewLoading}
                            empty={
                                <Box
                                    margin={{ vertical: "xs" }}
                                    textAlign="center"
                                    color="inherit"
                                >
                                    <SpaceBetween size="m">
                                        <b>No items</b>
                                    </SpaceBetween>
                                </Box>
                            }
                            onSortingChange={collectionProps.onSortingChange}
                            sortingColumn={collectionProps.sortingColumn}
                            ref={collectionProps.ref}
                        />
                    </ExpandableSection>
                    <div id={TRANSACTION_CONTAINER_DOM_ID}>
                        { showTransactions && 
                            <TransactionsAdjustment
                                hideTransactionsHandler={hideTransactionsHandler}
                                stepsState={stepsState}
                                currentFilter={currentFilter}
                                obligationStatus={obligationStatus}
                                filterData={transactionFilterData}
                                refreshSummaryData={refreshSummaryData}
                                setIsLoadingSummary={setIsTransactionReviewLoading}
                                parentWorkflowId={parentWorkflowId}
                                useCase={useCase}
                                reportingPeriod={reportingPeriod}
                                useCaseType={useCaseType}
                                phastosUseCaseName={phastosUseCaseName}
                            />
                        }  
                    </div>
                </SpaceBetween>
            }
            { !transactionReviewEditMode &&
                <QuickSightFrame linkKey={'EXCEPTION_REVIEW'}
                                 exceptionData={exceptionData}
                                 useCase={useCase}/>
            }
            </Container>
        </SpaceBetween>
    );
}